import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  cryptoPortfolioService,
  FilterProfileConfig,
  TradingFilterProfile,
} from '../../services/cryptoPortfolioService';
import {
  buildFilterProfileImportCreatePayload,
  downloadFilterProfileExport,
  parseFilterProfileExportJson,
} from '../../utils/filterProfileExportImport';
import { fieldInputStyle, fieldLabelStyle } from './ActivityBotSizingControls';

interface FilterProfileDetailRow {
  section: string;
  label: string;
  value: string;
}

function onOff(enabled: boolean | undefined): string {
  return enabled ? 'On' : 'Off';
}

function pctRate(value: number | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

function describeFilterProfileConfigDetails(config?: FilterProfileConfig | null): FilterProfileDetailRow[] {
  if (!config) {
    return [{ section: 'Configuration', label: 'Status', value: 'No configuration stored' }];
  }

  const rows: FilterProfileDetailRow[] = [];
  const entry = config.entry ?? {};

  rows.push({ section: 'Entry filters', label: 'Technical entry filters', value: onOff(entry.enabled) });
  if (entry.enabled) {
    rows.push({
      section: 'Entry filters',
      label: 'RSI alignment',
      value: entry.rsiEnabled === false
        ? 'Off'
        : `On — long min ${entry.rsiLongMin ?? 55}, short max ${entry.rsiShortMax ?? 45}`,
    });
    rows.push({
      section: 'Entry filters',
      label: 'EMA trend',
      value: entry.emaEnabled === false
        ? 'Off'
        : `On — period ${entry.emaPeriod ?? 20}`,
    });
    rows.push({
      section: 'Entry filters',
      label: 'Funding rate cap',
      value: entry.fundingRateEnabled === false
        ? 'Off'
        : `On — max ${entry.maxFundingRate ?? 0.0005}`,
    });
    rows.push({
      section: 'Entry filters',
      label: 'Peak/trough timing',
      value: onOff(entry.peakTroughTimingEnabled),
    });
  }

  const marketGates = config.marketGates ?? {};
  rows.push({
    section: 'Market gates',
    label: 'Participation gate',
    value: marketGates.participationEnabled
      ? `On — min ${pctRate(marketGates.minParticipationRate ?? 0.25)}`
      : 'Off',
  });
  rows.push({
    section: 'Market gates',
    label: '₿ Major coin participation',
    value: marketGates.majorCoinParticipationEnabled
      ? `On — min ${pctRate(marketGates.minMajorCoinParticipationRate ?? 0.25)}`
      : 'Off',
  });

  const oracle = config.oracle ?? {};
  rows.push({
    section: 'Coin selection oracle',
    label: 'Oracle ranking',
    value: oracle.enabled
      ? `On — min confidence ${oracle.confidenceThreshold ?? 0.5}, shortlist ${oracle.shortlistSize ?? 20}`
      : 'Off',
  });

  const microReversal = config.microReversal ?? {};
  rows.push({
    section: 'Micro-reversal',
    label: 'BTC 5m micro-reversal block',
    value: microReversal.enabled
      ? `On — drop ${microReversal.minDropPercent ?? -0.5}% to ${microReversal.maxDropPercent ?? -0.1}%, lookback ${microReversal.lookbackCandles ?? 12} candles`
      : 'Off',
  });

  const volatility = config.volatility ?? {};
  rows.push({
    section: 'Volatility band',
    label: 'Meme-market volatility band',
    value: volatility.enabled
      ? `On — min ${volatility.minMarketVolatility ?? 'any'}%, max ${volatility.maxMarketVolatility ?? 'any'}%`
      : 'Off',
  });

  const btcDump = config.btcDump ?? {};
  rows.push({
    section: 'BTC dump protection',
    label: 'Pause after BTC drop',
    value: btcDump.enabled
      ? `On — threshold ${btcDump.dumpThreshold ?? 1.5}%, cooldown ${btcDump.cooldownMinutes ?? 20} min`
      : 'Off',
  });

  const lossCooldown = config.lossCooldown ?? {};
  rows.push({
    section: 'Consecutive loss cooldown',
    label: 'Pause after consecutive losses',
    value: lossCooldown.enabled
      ? `On — ${lossCooldown.consecutiveLosses ?? 2} losses in ${lossCooldown.lossWindowHours ?? 24}h → cooldown ${lossCooldown.cooldownHours ?? 24}h`
      : 'Off',
  });

  const regime = config.regime ?? {};
  rows.push({
    section: 'Regime gates',
    label: 'Block opens in low participation',
    value: onOff(regime.blockOpensInLowParticipation),
  });
  rows.push({
    section: 'Regime gates',
    label: 'Block opens in bearish BTC',
    value: onOff(regime.blockOpensInBearishBtc),
  });
  rows.push({
    section: 'Regime gates',
    label: 'Block long legs in bearish BTC',
    value: onOff(regime.blockLongInBearishBtc),
  });
  rows.push({
    section: 'Regime gates',
    label: 'Block short legs unless BTC bearish',
    value: onOff(regime.blockShortUnlessBearishBtc),
  });

  return rows;
}

function summarizeFilterProfileBrief(config?: FilterProfileConfig | null): string {
  if (!config) return 'No configuration';
  const tags: string[] = [];
  const entry = config.entry;
  if (entry?.enabled) {
    const parts: string[] = [];
    if (entry.rsiEnabled !== false) parts.push('RSI');
    if (entry.emaEnabled !== false) parts.push('EMA');
    if (entry.fundingRateEnabled !== false) parts.push('Funding');
    if (entry.peakTroughTimingEnabled) parts.push('Peak/trough');
    if (parts.length > 0) tags.push(`Entry: ${parts.join(', ')}`);
  }
  if (config.marketGates?.participationEnabled) tags.push('Meme participation');
  if (config.marketGates?.majorCoinParticipationEnabled) tags.push('Major coin participation');
  if (config.oracle?.enabled) tags.push('Oracle ranking');
  if (config.microReversal?.enabled) tags.push('Micro-reversal');
  if (config.volatility?.enabled) tags.push('Volatility band');
  if (config.btcDump?.enabled) tags.push('BTC dump protection');
  if (config.lossCooldown?.enabled) tags.push('Loss cooldown');
  const regime = config.regime;
  if (regime?.blockOpensInLowParticipation || regime?.blockOpensInBearishBtc || regime?.blockLongInBearishBtc || regime?.blockShortUnlessBearishBtc) {
    tags.push('Regime gates');
  }
  return tags.length > 0 ? tags.join(' · ') : 'No filters enabled';
}

function FilterProfileConfigDetails({
  config,
  compact = false,
}: {
  config?: FilterProfileConfig | null;
  compact?: boolean;
}) {
  const rows = describeFilterProfileConfigDetails(config);
  const sections = Array.from(new Set(rows.map(row => row.section)));

  return (
    <div
      style={{
        marginTop: compact ? 8 : 10,
        padding: compact ? '10px 12px' : '12px 14px',
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        background: '#fff',
      }}
    >
      {sections.map(section => (
        <div key={section} style={{ marginBottom: section === sections[sections.length - 1] ? 0 : 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
            {section}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {rows.filter(row => row.section === section).map(row => (
              <div
                key={`${section}-${row.label}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(140px, 42%) 1fr',
                  gap: '4px 12px',
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                <span style={{ color: '#64748b' }}>{row.label}</span>
                <span style={{ color: '#1e293b', fontWeight: 600 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export interface FilterProfileEditorDraft {
  name: string;
  description: string;
  config: FilterProfileConfig;
}

function defaultFilterProfileConfig(): FilterProfileConfig {
  return {
    schemaVersion: 1,
    entry: {
      enabled: true,
      fundingRateEnabled: true,
      maxFundingRate: 0.0005,
      rsiEnabled: true,
      rsiLongMin: 55,
      rsiShortMax: 45,
      emaEnabled: true,
      emaPeriod: 20,
      peakTroughTimingEnabled: false,
    },
    marketGates: {
      participationEnabled: false,
      minParticipationRate: 0.25,
      majorCoinParticipationEnabled: false,
      minMajorCoinParticipationRate: 0.25,
    },
    regime: {
      blockOpensInLowParticipation: false,
      blockOpensInBearishBtc: false,
      blockLongInBearishBtc: false,
      blockShortUnlessBearishBtc: false,
    },
    microReversal: {
      enabled: false,
      minDropPercent: -0.5,
      maxDropPercent: -0.1,
      lookbackCandles: 12,
    },
    volatility: {
      enabled: false,
      minMarketVolatility: undefined,
      maxMarketVolatility: undefined,
    },
    btcDump: {
      enabled: false,
      dumpThreshold: 1.5,
      cooldownMinutes: 20,
    },
    lossCooldown: {
      enabled: false,
      consecutiveLosses: 2,
      lossWindowHours: 24,
      cooldownHours: 24,
    },
    oracle: {
      enabled: false,
      confidenceThreshold: 0.5,
      weightMomentum: null,
      weightRsi: null,
      weightVolume: null,
      weightFunding: null,
      weightEma: null,
      shortlistSize: 20,
    },
  };
}

function cloneFilterProfileConfig(config?: FilterProfileConfig | null): FilterProfileConfig {
  return JSON.parse(JSON.stringify(config ?? defaultFilterProfileConfig())) as FilterProfileConfig;
}

function emptyFilterProfileEditorDraft(): FilterProfileEditorDraft {
  return { name: '', description: '', config: defaultFilterProfileConfig() };
}

const filterEditorSectionStyle: React.CSSProperties = {
  padding: '12px 14px',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  background: '#f8fafc',
};

const filterEditorCheckLabel: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  color: '#334155',
  cursor: 'pointer',
};

function FilterProfileEditorForm({
  draft,
  onChange,
  disabled,
}: {
  draft: FilterProfileEditorDraft;
  onChange: (draft: FilterProfileEditorDraft) => void;
  disabled?: boolean;
}) {
  const entry = draft.config.entry ?? {};
  const marketGates = draft.config.marketGates ?? {};
  const regime = draft.config.regime ?? {};
  const microReversal = draft.config.microReversal ?? {};
  const volatility = draft.config.volatility ?? {};
  const btcDump = draft.config.btcDump ?? {};
  const lossCooldown = draft.config.lossCooldown ?? {};
  const oracle = draft.config.oracle ?? {};

  const patchEntry = (patch: Partial<NonNullable<FilterProfileConfig['entry']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        entry: { ...entry, ...patch },
      },
    });
  };

  const patchMarketGates = (patch: Partial<NonNullable<FilterProfileConfig['marketGates']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        marketGates: { ...marketGates, ...patch },
      },
    });
  };

  const patchRegime = (patch: Partial<NonNullable<FilterProfileConfig['regime']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        regime: { ...regime, ...patch },
      },
    });
  };

  const patchMicroReversal = (patch: Partial<NonNullable<FilterProfileConfig['microReversal']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        microReversal: { ...microReversal, ...patch },
      },
    });
  };

  const patchVolatility = (patch: Partial<NonNullable<FilterProfileConfig['volatility']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        volatility: { ...volatility, ...patch },
      },
    });
  };

  const patchBtcDump = (patch: Partial<NonNullable<FilterProfileConfig['btcDump']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        btcDump: { ...btcDump, ...patch },
      },
    });
  };

  const patchLossCooldown = (patch: Partial<NonNullable<FilterProfileConfig['lossCooldown']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        lossCooldown: { ...lossCooldown, ...patch },
      },
    });
  };

  const patchOracle = (patch: Partial<NonNullable<FilterProfileConfig['oracle']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        oracle: { ...oracle, ...patch },
      },
    });
  };

  const numInputStyle: React.CSSProperties = {
    ...fieldInputStyle,
    width: 88,
    padding: '6px 8px',
    fontSize: 12,
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <label style={fieldLabelStyle}>Profile name</label>
        <input
          type="text"
          maxLength={120}
          value={draft.name}
          disabled={disabled}
          onChange={e => onChange({ ...draft, name: e.target.value })}
          placeholder="e.g. My gaming entry filters"
          style={fieldInputStyle}
        />
      </div>
      <div>
        <label style={fieldLabelStyle}>Description (optional)</label>
        <input
          type="text"
          maxLength={512}
          value={draft.description}
          disabled={disabled}
          onChange={e => onChange({ ...draft, description: e.target.value })}
          placeholder="Short note for your team"
          style={fieldInputStyle}
        />
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Entry filters</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(entry.enabled)}
            disabled={disabled}
            onChange={e => patchEntry({ enabled: e.target.checked })}
          />
          Enable technical entry filters
        </label>
        {entry.enabled && (
          <div style={{ display: 'grid', gap: 10, marginTop: 12, paddingLeft: 4 }}>
            <label style={filterEditorCheckLabel}>
              <input
                type="checkbox"
                checked={entry.rsiEnabled !== false}
                disabled={disabled}
                onChange={e => patchEntry({ rsiEnabled: e.target.checked })}
              />
              RSI alignment
            </label>
            {entry.rsiEnabled !== false && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingLeft: 24 }}>
                <div>
                  <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Long min RSI</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    disabled={disabled}
                    value={entry.rsiLongMin ?? 55}
                    onChange={e => patchEntry({ rsiLongMin: Number(e.target.value) })}
                    style={numInputStyle}
                  />
                </div>
                <div>
                  <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Short max RSI</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    disabled={disabled}
                    value={entry.rsiShortMax ?? 45}
                    onChange={e => patchEntry({ rsiShortMax: Number(e.target.value) })}
                    style={numInputStyle}
                  />
                </div>
              </div>
            )}
            <label style={filterEditorCheckLabel}>
              <input
                type="checkbox"
                checked={entry.emaEnabled !== false}
                disabled={disabled}
                onChange={e => patchEntry({ emaEnabled: e.target.checked })}
              />
              EMA trend
            </label>
            {entry.emaEnabled !== false && (
              <div style={{ paddingLeft: 24 }}>
                <label style={{ ...fieldLabelStyle, fontSize: 11 }}>EMA period</label>
                <input
                  type="number"
                  min={2}
                  max={200}
                  step={1}
                  disabled={disabled}
                  value={entry.emaPeriod ?? 20}
                  onChange={e => patchEntry({ emaPeriod: Number(e.target.value) })}
                  style={numInputStyle}
                />
              </div>
            )}
            <label style={filterEditorCheckLabel}>
              <input
                type="checkbox"
                checked={entry.fundingRateEnabled !== false}
                disabled={disabled}
                onChange={e => patchEntry({ fundingRateEnabled: e.target.checked })}
              />
              Funding rate cap
            </label>
            {entry.fundingRateEnabled !== false && (
              <div style={{ paddingLeft: 24 }}>
                <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Max funding rate</label>
                <input
                  type="number"
                  min={0}
                  step={0.0001}
                  disabled={disabled}
                  value={entry.maxFundingRate ?? 0.0005}
                  onChange={e => patchEntry({ maxFundingRate: Number(e.target.value) })}
                  style={{ ...numInputStyle, width: 120 }}
                />
              </div>
            )}
            <label style={filterEditorCheckLabel}>
              <input
                type="checkbox"
                checked={Boolean(entry.peakTroughTimingEnabled)}
                disabled={disabled}
                onChange={e => patchEntry({ peakTroughTimingEnabled: e.target.checked })}
              />
              Peak/trough timing (wait for better entry)
            </label>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Market gates</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(marketGates.participationEnabled)}
            disabled={disabled}
            onChange={e => patchMarketGates({ participationEnabled: e.target.checked })}
          />
          Participation gate (meme sector activity)
        </label>
        {marketGates.participationEnabled && (
          <div style={{ marginTop: 10, paddingLeft: 24 }}>
            <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min participation rate</label>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              disabled={disabled}
              value={marketGates.minParticipationRate ?? 0.25}
              onChange={e => patchMarketGates({ minParticipationRate: Number(e.target.value) })}
              style={{ ...numInputStyle, width: 100 }}
            />
          </div>
        )}
        <div
          style={{
            marginTop: 14,
            padding: '12px 14px',
            borderRadius: 8,
            border: `1px solid ${marketGates.majorCoinParticipationEnabled ? '#a5f3fc' : '#e2e8f0'}`,
            background: marketGates.majorCoinParticipationEnabled ? '#f0fdff' : '#f8fafc',
          }}
        >
          <label style={{ ...filterEditorCheckLabel, marginBottom: 6 }}>
            <input
              type="checkbox"
              checked={Boolean(marketGates.majorCoinParticipationEnabled)}
              disabled={disabled}
              onChange={e => patchMarketGates({ majorCoinParticipationEnabled: e.target.checked })}
            />
            ₿ Major Coin Participation Filter
          </label>
          <p style={{ margin: '0 0 8px 24px', fontSize: 11, color: '#64748b', lineHeight: 1.45 }}>
            Blocks new legs when top major coins (TOP 100 futures universe) have low participation — weak
            follow-through (positive 24h change + above-median volume). Same logic as crypto trading bots.
          </p>
          {marketGates.majorCoinParticipationEnabled ? (
            <div style={{ paddingLeft: 24 }}>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min active major coins</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="number"
                  min={5}
                  max={100}
                  step={1}
                  disabled={disabled}
                  value={Math.round((marketGates.minMajorCoinParticipationRate ?? 0.25) * 100)}
                  onChange={e => {
                    const pct = Number(e.target.value);
                    if (!Number.isFinite(pct)) return;
                    patchMarketGates({ minMajorCoinParticipationRate: Math.max(0.05, Math.min(1, pct / 100)) });
                  }}
                  style={{ ...numInputStyle, width: 72 }}
                />
                <span style={{ fontSize: 12, color: '#64748b' }}>% of universe must be active</span>
              </div>
            </div>
          ) : (
            <p style={{ margin: '4px 0 0 24px', fontSize: 11, color: '#94a3b8' }}>
              Disabled — legs open regardless of major-coin participation.
            </p>
          )}
        </div>
      </div>

      <div
        style={{
          ...filterEditorSectionStyle,
          border: `1px solid ${oracle.enabled ? '#93c5fd' : '#e2e8f0'}`,
          background: oracle.enabled ? '#eff6ff' : '#f8fafc',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1d4ed8' }}>Coin Selection Oracle</div>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#64748b', lineHeight: 1.45, maxWidth: 520 }}>
              Scores coins in the bot&apos;s sector on RSI, momentum, volume, funding rate &amp; EMA — re-ranks
              candidates before opening legs. Falls back to activity ranking if confidence is too low.
            </p>
          </div>
          <label style={{ ...filterEditorCheckLabel, flexShrink: 0, color: oracle.enabled ? '#1d4ed8' : '#64748b' }}>
            <input
              type="checkbox"
              checked={Boolean(oracle.enabled)}
              disabled={disabled}
              onChange={e => patchOracle({ enabled: e.target.checked })}
            />
            {oracle.enabled ? 'Enabled' : 'Disabled'}
          </label>
        </div>
        <div style={{ opacity: oracle.enabled ? 1 : 0.45, pointerEvents: oracle.enabled ? 'auto' : 'none' }}>
          <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min confidence threshold (0.0 – 1.0)</label>
          <input
            type="number"
            step={0.05}
            min={0.1}
            max={1}
            disabled={disabled}
            value={oracle.confidenceThreshold ?? 0.5}
            onChange={e => {
              const v = Number(e.target.value);
              if (Number.isFinite(v) && v >= 0 && v <= 1) patchOracle({ confidenceThreshold: v });
            }}
            style={{ ...fieldInputStyle, maxWidth: 120, marginTop: 4 }}
          />
          <p style={{ margin: '4px 0 12px', fontSize: 11, color: '#64748b' }}>
            Below this top side score, activity ranking is used instead. Default: 0.50
          </p>
          <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Shortlist size (activity-ranked coins to score)</label>
          <input
            type="number"
            min={2}
            max={40}
            step={1}
            disabled={disabled}
            value={oracle.shortlistSize ?? 20}
            onChange={e => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) patchOracle({ shortlistSize: Math.max(2, Math.min(40, n)) });
            }}
            style={{ ...numInputStyle, width: 72, marginTop: 4 }}
          />
          <p style={{ margin: '12px 0 8px', fontSize: 12, fontWeight: 600, color: '#1e3a8a' }}>
            Signal weights <span style={{ fontWeight: 400, color: '#64748b' }}>(blank = global defaults)</span>
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {(
              [
                { key: 'weightMomentum' as const, label: 'Momentum', def: 0.25 },
                { key: 'weightRsi' as const, label: 'RSI', def: 0.3 },
                { key: 'weightVolume' as const, label: 'Volume', def: 0.2 },
                { key: 'weightFunding' as const, label: 'Funding rate', def: 0.15 },
                { key: 'weightEma' as const, label: 'EMA trend', def: 0.1 },
              ] as const
            ).map(({ key, label, def }) => (
              <div key={key}>
                <label style={{ ...fieldLabelStyle, fontSize: 11 }}>{label}</label>
                <input
                  type="number"
                  step={0.05}
                  min={0}
                  max={1}
                  disabled={disabled}
                  placeholder={`Global (${def})`}
                  value={oracle[key] ?? ''}
                  onChange={e => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patchOracle({ [key]: null });
                      return;
                    }
                    const v = Number(raw);
                    if (Number.isFinite(v) && v >= 0 && v <= 1) patchOracle({ [key]: v });
                  }}
                  style={{ ...numInputStyle, width: '100%' }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Micro-reversal (BTC 5m)</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(microReversal.enabled)}
            disabled={disabled}
            onChange={e => patchMicroReversal({ enabled: e.target.checked })}
          />
          Block entries in BTC micro-reversal window
        </label>
        {microReversal.enabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min drop %</label>
              <input
                type="number"
                step={0.01}
                disabled={disabled}
                value={microReversal.minDropPercent ?? -0.5}
                onChange={e => patchMicroReversal({ minDropPercent: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Max drop %</label>
              <input
                type="number"
                step={0.01}
                disabled={disabled}
                value={microReversal.maxDropPercent ?? -0.1}
                onChange={e => patchMicroReversal({ maxDropPercent: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Lookback candles</label>
              <input
                type="number"
                min={3}
                max={60}
                step={1}
                disabled={disabled}
                value={microReversal.lookbackCandles ?? 12}
                onChange={e => patchMicroReversal({ lookbackCandles: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Volatility band</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(volatility.enabled)}
            disabled={disabled}
            onChange={e => patchVolatility({ enabled: e.target.checked })}
          />
          Require meme-market volatility within min/max %
        </label>
        {volatility.enabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min % (optional)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                disabled={disabled}
                value={volatility.minMarketVolatility ?? ''}
                onChange={e => patchVolatility({
                  minMarketVolatility: e.target.value === '' ? undefined : Number(e.target.value),
                })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Max % (optional)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                disabled={disabled}
                value={volatility.maxMarketVolatility ?? ''}
                onChange={e => patchVolatility({
                  maxMarketVolatility: e.target.value === '' ? undefined : Number(e.target.value),
                })}
                style={numInputStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>BTC dump protection</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(btcDump.enabled)}
            disabled={disabled}
            onChange={e => patchBtcDump({ enabled: e.target.checked })}
          />
          Pause opens after sharp BTC drop (10m)
        </label>
        {btcDump.enabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Dump threshold %</label>
              <input
                type="number"
                min={0.1}
                step={0.1}
                disabled={disabled}
                value={btcDump.dumpThreshold ?? 1.5}
                onChange={e => patchBtcDump({ dumpThreshold: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Cooldown minutes</label>
              <input
                type="number"
                min={1}
                max={240}
                step={1}
                disabled={disabled}
                value={btcDump.cooldownMinutes ?? 20}
                onChange={e => patchBtcDump({ cooldownMinutes: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>
          Consecutive loss cooldown
        </div>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
          Reactive risk gate (not an entry filter). After consecutive losing closes within the window,
          the bot pauses new opens for the cooldown period — even if other filters would allow entry.
        </p>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(lossCooldown.enabled)}
            disabled={disabled}
            onChange={e => patchLossCooldown({ enabled: e.target.checked })}
          />
          Pause opens after consecutive losses
        </label>
        {lossCooldown.enabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Consecutive losses</label>
              <input
                type="number"
                min={2}
                max={10}
                step={1}
                disabled={disabled}
                value={lossCooldown.consecutiveLosses ?? 2}
                onChange={e => patchLossCooldown({ consecutiveLosses: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Loss window (hours)</label>
              <input
                type="number"
                min={1}
                max={168}
                step={1}
                disabled={disabled}
                value={lossCooldown.lossWindowHours ?? 24}
                onChange={e => patchLossCooldown({ lossWindowHours: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Cooldown (hours)</label>
              <input
                type="number"
                min={1}
                max={168}
                step={1}
                disabled={disabled}
                value={lossCooldown.cooldownHours ?? 24}
                onChange={e => patchLossCooldown({ cooldownHours: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Regime gates</div>
        <div style={{ display: 'grid', gap: 8 }}>
          <label style={filterEditorCheckLabel}>
            <input
              type="checkbox"
              checked={Boolean(regime.blockOpensInLowParticipation)}
              disabled={disabled}
              onChange={e => patchRegime({ blockOpensInLowParticipation: e.target.checked })}
            />
            Block opens in low participation
          </label>
          <label style={filterEditorCheckLabel}>
            <input
              type="checkbox"
              checked={Boolean(regime.blockOpensInBearishBtc)}
              disabled={disabled}
              onChange={e => patchRegime({ blockOpensInBearishBtc: e.target.checked })}
            />
            Block opens in bearish BTC regime
          </label>
          <label style={filterEditorCheckLabel}>
            <input
              type="checkbox"
              checked={Boolean(regime.blockLongInBearishBtc)}
              disabled={disabled}
              onChange={e => patchRegime({ blockLongInBearishBtc: e.target.checked })}
            />
            Block long legs in bearish BTC (shorts still allowed)
          </label>
          <label style={filterEditorCheckLabel}>
            <input
              type="checkbox"
              checked={Boolean(regime.blockShortUnlessBearishBtc)}
              disabled={disabled}
              onChange={e => patchRegime({ blockShortUnlessBearishBtc: e.target.checked })}
            />
            Block short legs unless BTC is bearish (longs still allowed)
          </label>
        </div>
      </div>
    </div>
  );
}


export interface ActivityBotFilterProfilesModalProps {
  open: boolean;
  onClose: () => void;
  profiles: TradingFilterProfile[];
  onProfilesChange: (profiles: TradingFilterProfile[]) => void;
  onUseProfile: (profileId: number) => void;
  cardStyle?: React.CSSProperties;
}

const defaultCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  boxShadow: '0 8px 30px rgba(15, 23, 42, 0.12)',
};

export const ActivityBotFilterProfilesModal: React.FC<ActivityBotFilterProfilesModalProps> = ({
  open,
  onClose,
  profiles,
  onProfilesChange,
  onUseProfile,
  cardStyle = defaultCardStyle,
}) => {
  const [profilesModalView, setProfilesModalView] = useState<'list' | 'edit'>('list');
  const [profileEditorReadOnly, setProfileEditorReadOnly] = useState(false);
  const [profileEditorId, setProfileEditorId] = useState<number | null>(null);
  const [profileEditorDraft, setProfileEditorDraft] = useState<FilterProfileEditorDraft>(emptyFilterProfileEditorDraft);
  const [profileEditorError, setProfileEditorError] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingProfileId, setDeletingProfileId] = useState<number | null>(null);
  const [cloningProfileId, setCloningProfileId] = useState<number | null>(null);
  const [importingProfile, setImportingProfile] = useState(false);
  const [expandedProfileConfigIds, setExpandedProfileConfigIds] = useState<Set<number>>(() => new Set());
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      setProfilesModalView('list');
      setProfileEditorReadOnly(false);
      setProfileEditorError(null);
      setImportingProfile(false);
    }
  }, [open]);

  const refreshProfiles = useCallback(async () => {
    const catalog = await cryptoPortfolioService.getFilterProfileCatalog();
    const rows = catalog.profiles ?? [];
    onProfilesChange(rows);
    return rows;
  }, [onProfilesChange]);

  const openNewProfileEditor = () => {
    setProfileEditorId(null);
    setProfileEditorReadOnly(false);
    setProfileEditorDraft(emptyFilterProfileEditorDraft());
    setProfileEditorError(null);
    setProfilesModalView('edit');
  };

  const openEditProfileEditor = (profile: TradingFilterProfile) => {
    setProfileEditorId(profile.id);
    setProfileEditorReadOnly(false);
    setProfileEditorDraft({
      name: profile.name || '',
      description: profile.description || '',
      config: cloneFilterProfileConfig(profile.config),
    });
    setProfileEditorError(null);
    setProfilesModalView('edit');
  };

  const openViewProfileEditor = (profile: TradingFilterProfile) => {
    setProfileEditorId(profile.id);
    setProfileEditorReadOnly(true);
    setProfileEditorDraft({
      name: profile.name || '',
      description: profile.description || '',
      config: cloneFilterProfileConfig(profile.config),
    });
    setProfileEditorError(null);
    setProfilesModalView('edit');
  };

  const handleSaveProfile = async () => {
    const name = profileEditorDraft.name.trim();
    if (!name) {
      setProfileEditorError('Profile name is required.');
      return;
    }
    setSavingProfile(true);
    setProfileEditorError(null);
    try {
      const payload = {
        name,
        description: profileEditorDraft.description.trim() || null,
        config: profileEditorDraft.config,
      };
      const saved = profileEditorId == null
        ? await cryptoPortfolioService.createFilterProfile(payload)
        : await cryptoPortfolioService.updateFilterProfile(profileEditorId, payload);
      await refreshProfiles();
      onUseProfile(saved.id);
      setProfilesModalView('list');
      onClose();
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to save profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteProfile = async (profileId: number) => {
    if (!window.confirm('Delete this custom profile? Activity bots using it will fall back to legacy entry behavior.')) {
      return;
    }
    setDeletingProfileId(profileId);
    setProfileEditorError(null);
    try {
      await cryptoPortfolioService.deleteFilterProfile(profileId);
      const rows = await refreshProfiles();
      if (profileEditorId === profileId) {
        setProfilesModalView('list');
        setProfileEditorId(null);
      }
      if (rows.length === 0) {
        onClose();
      }
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to delete profile.');
    } finally {
      setDeletingProfileId(null);
    }
  };

  const handleCloneProfile = async (profileId: number) => {
    setCloningProfileId(profileId);
    setProfileEditorError(null);
    try {
      const cloned = await cryptoPortfolioService.cloneFilterProfile(profileId);
      await refreshProfiles();
      onUseProfile(cloned.id);
      setProfilesModalView('list');
      onClose();
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to clone profile.');
    } finally {
      setCloningProfileId(null);
    }
  };

  const handleExportProfile = (profile: TradingFilterProfile) => {
    try {
      downloadFilterProfileExport(profile);
      setProfileEditorError(null);
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to export profile.');
    }
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportingProfile(true);
    setProfileEditorError(null);
    try {
      const raw = await file.text();
      const parsed = parseFilterProfileExportJson(raw);
      const payload = buildFilterProfileImportCreatePayload(
        parsed,
        profiles.map(p => p.name),
      );
      const created = await cryptoPortfolioService.createFilterProfile(payload);
      await refreshProfiles();
      onUseProfile(created.id);
      setProfilesModalView('list');
      onClose();
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to import profile.');
    } finally {
      setImportingProfile(false);
    }
  };

  const toggleProfileConfigExpanded = (profileId: number) => {
    setExpandedProfileConfigIds(prev => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1400,
      }}
      onClick={() => {
        onClose();
        setProfilesModalView('list');
        setProfileEditorReadOnly(false);
      }}
    >
      <div
        style={{
          ...cardStyle,
          width: '100%',
          maxWidth: 620,
          maxHeight: '85vh',
          overflow: 'auto',
          padding: 18,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>
              {profilesModalView === 'edit'
                ? (profileEditorReadOnly
                  ? 'View filter profile'
                  : (profileEditorId == null ? 'Create filter profile' : 'Edit filter profile'))
                : 'Filter profiles'}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
              {profilesModalView === 'edit'
                ? (profileEditorReadOnly
                  ? 'Read-only view of this profile’s settings. Clone a system template to customize it.'
                  : 'Customize entry, market, and regime gates. System templates stay read-only — clone first to edit.')
                : 'Reusable entry filters for activity bots. System templates are read-only and cannot be deleted. Export/import shares custom configs as JSON.'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onClose();
              setProfilesModalView('list');
              setProfileEditorReadOnly(false);
            }}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 18,
              color: '#94a3b8',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {profileEditorError && (
          <div
            role="alert"
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              fontSize: 12,
            }}
          >
            {profileEditorError}
          </div>
        )}

        {profilesModalView === 'edit' ? (
          <>
            <FilterProfileEditorForm
              draft={profileEditorDraft}
              onChange={setProfileEditorDraft}
              disabled={savingProfile || profileEditorReadOnly}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              {!profileEditorReadOnly && (
                <button
                  type="button"
                  disabled={savingProfile}
                  onClick={() => void handleSaveProfile()}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 999,
                    border: 'none',
                    background: '#086afb',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: savingProfile ? 'wait' : 'pointer',
                    opacity: savingProfile ? 0.7 : 1,
                  }}
                >
                  {savingProfile ? 'Saving…' : 'Save profile'}
                </button>
              )}
              <button
                type="button"
                disabled={savingProfile}
                onClick={() => {
                  setProfilesModalView('list');
                  setProfileEditorReadOnly(false);
                  setProfileEditorError(null);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 999,
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: '#334155',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Back to list
              </button>
              {!profileEditorReadOnly && profileEditorId != null && (
                <button
                  type="button"
                  disabled={savingProfile || deletingProfileId === profileEditorId}
                  onClick={() => void handleDeleteProfile(profileEditorId)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 999,
                    border: '1px solid #fecaca',
                    background: '#fff',
                    color: '#b91c1c',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: deletingProfileId === profileEditorId ? 'wait' : 'pointer',
                    marginLeft: 'auto',
                  }}
                >
                  {deletingProfileId === profileEditorId ? 'Deleting…' : 'Delete profile'}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                onClick={openNewProfileEditor}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: 'none',
                  background: '#086afb',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                + Create custom profile
              </button>
              <button
                type="button"
                disabled={importingProfile}
                onClick={() => importFileInputRef.current?.click()}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: '#334155',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: importingProfile ? 'wait' : 'pointer',
                  opacity: importingProfile ? 0.7 : 1,
                }}
              >
                {importingProfile ? 'Importing…' : 'Import JSON'}
              </button>
              <input
                ref={importFileInputRef}
                type="file"
                accept="application/json,.json"
                style={{ display: 'none' }}
                onChange={e => void handleImportFileChange(e)}
              />
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {profiles.map(profile => {
                const configExpanded = expandedProfileConfigIds.has(profile.id);
                return (
                  <div
                    key={profile.id}
                    style={{
                      padding: '12px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      background: '#f8fafc',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
                      {profile.name}
                      {profile.systemTemplate && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b' }}>(system template)</span>
                      )}
                    </div>
                    {profile.description && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{profile.description}</div>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleProfileConfigExpanded(profile.id)}
                      aria-expanded={configExpanded}
                      style={{
                        width: '100%',
                        marginTop: 10,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#334155',
                        textAlign: 'left',
                      }}
                    >
                      <span>Filter configuration</span>
                      <span style={{ color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>{configExpanded ? '▴' : '▾'}</span>
                    </button>
                    {configExpanded ? (
                      <FilterProfileConfigDetails config={profile.config} compact />
                    ) : (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                        {summarizeFilterProfileBrief(profile.config)}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => {
                          onUseProfile(profile.id);
                          onClose();
                          setProfilesModalView('list');
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 999,
                          border: 'none',
                          background: '#086afb',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Use profile
                      </button>
                      <button
                        type="button"
                        onClick={() => handleExportProfile(profile)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 999,
                          border: '1px solid #e2e8f0',
                          background: '#fff',
                          color: '#334155',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Export JSON
                      </button>
                      {profile.systemTemplate ? (
                        <>
                          <button
                            type="button"
                            onClick={() => openViewProfileEditor(profile)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 999,
                              border: '1px solid #e2e8f0',
                              background: '#fff',
                              color: '#334155',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            View full form
                          </button>
                          <button
                            type="button"
                            disabled={cloningProfileId === profile.id}
                            onClick={() => void handleCloneProfile(profile.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 999,
                              border: '1px solid #e2e8f0',
                              background: '#fff',
                              color: '#334155',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: cloningProfileId === profile.id ? 'wait' : 'pointer',
                            }}
                          >
                            {cloningProfileId === profile.id ? 'Cloning…' : 'Clone to my profiles'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => openEditProfileEditor(profile)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 999,
                              border: '1px solid #e2e8f0',
                              background: '#fff',
                              color: '#334155',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            disabled={deletingProfileId === profile.id}
                            onClick={() => void handleDeleteProfile(profile.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 999,
                              border: '1px solid #fecaca',
                              background: '#fff',
                              color: '#b91c1c',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: deletingProfileId === profile.id ? 'wait' : 'pointer',
                            }}
                          >
                            {deletingProfileId === profile.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {profiles.length === 0 && (
                <div style={{ fontSize: 13, color: '#64748b' }}>No filter profiles yet. Create a custom profile to get started.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { FilterProfileConfigDetails, emptyFilterProfileEditorDraft, cloneFilterProfileConfig };
